/**
 * Coach Engine — Observation Muting
 *
 * Lets the trader temporarily silence specific observations they've already
 * acted on or judged irrelevant. The muted set lives in settings.json as a
 * map of `observationId → expiresAtIso` so it survives reloads and respects
 * a user-chosen duration ("mute for 7 days" is the default).
 *
 * Two helpers:
 *   - `filterMuted(observations, mutedMap)` → drops observations whose id
 *     is in the muted map AND not yet expired.
 *   - `enrichWithMuteAction(observations, opts)` → appends a Mute action
 *     to each observation so the trader can mute it from the card itself.
 *
 * Both are pure. The state lives in TradingJournal; the dispatcher there
 * applies new mutes (action.type === 'mute-observation').
 *
 * Why a separate module: the filter and the action emitter are conceptually
 * paired but logically independent — call sites can opt into one without
 * the other (e.g., apply the filter without showing the Mute button).
 */

import { ACTION_TYPE, makeAction } from './types.js';

/**
 * Drop observations whose id is currently muted.
 *
 * @param {Array<Observation>} observations
 * @param {Object<string,string>} mutedMap  observationId → ISO expiresAt
 * @returns {Array<Observation>}
 */
export const filterMuted = (observations, mutedMap) => {
  if (!Array.isArray(observations)) return observations;
  if (!mutedMap || typeof mutedMap !== 'object') return observations;
  const now = Date.now();
  return observations.filter(o => {
    const exp = mutedMap[o?.id];
    if (!exp) return true;
    const expMs = Date.parse(exp);
    if (!Number.isFinite(expMs)) return true;  // bad data → unmute
    return expMs < now;                         // expired → unmute
  });
};

/**
 * Garbage-collect expired entries from a muted-observations map. Returns a
 * NEW object (no mutation) — caller persists if it shrunk.
 */
export const cleanExpiredMutes = (mutedMap) => {
  if (!mutedMap || typeof mutedMap !== 'object') return {};
  const now = Date.now();
  const out = {};
  for (const [id, exp] of Object.entries(mutedMap)) {
    const expMs = Date.parse(exp);
    if (Number.isFinite(expMs) && expMs >= now) out[id] = exp;
  }
  return out;
};

/**
 * Append a Mute action to each observation. Default duration is 7 days.
 * Skipped for observations that are themselves the mute action's target
 * (we don't render "Mute the muted notice" loops).
 *
 * @param {Array<Observation>} observations
 * @param {Object} [opts]
 * @param {number} [opts.days=7]
 * @returns {Array<Observation>}
 */
export const enrichWithMuteAction = (observations, opts = {}) => {
  if (!Array.isArray(observations)) return observations;
  const { days = 7 } = opts;
  return observations.map(o => {
    if (!o || !o.id) return o;
    // Don't add the mute action to observations that are already the
    // result of having BEEN muted (defensive — none currently exist, but
    // this protects the loop if a future surface emits muted-status notices).
    if (o.id.endsWith(':muted')) return o;
    const existing = Array.isArray(o.actions) ? o.actions : [];
    return {
      ...o,
      actions: [
        ...existing,
        makeAction({
          type: ACTION_TYPE.MUTE_OBSERVATION,
          label: `Mute ${days}d`,
          payload: { observationId: o.id, days },
          // No confirm — mute is reversible (just wait it out, or clear
          // mutes from Settings)
          confirmText: null,
          doneLabel: 'Muted ✓',
        }),
      ],
    };
  });
};

/**
 * Compute the ISO expiresAt timestamp for a mute action with `days`
 * duration from `now`. Pure — easy to test against fixed timestamps.
 *
 * @param {number} days
 * @param {number} [nowMs=Date.now()]
 * @returns {string} ISO 8601
 */
export const muteExpiry = (days, nowMs) => {
  const base = Number.isFinite(nowMs) ? nowMs : Date.now();
  const ms = base + Math.max(0, days) * 24 * 60 * 60 * 1000;
  return new Date(ms).toISOString();
};
